/**
 * NotificationDrawer
 *
 * Slide-in right-side drawer showing real-time case alerts.
 * Subscribes to Socket.IO `case:alert` and `alert:new` events.
 * Stores up to 50 notifications. Supports mark-read and navigate-to-case.
 */
import React, { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { getAccessToken } from "../services/auth.js";

export interface Notification {
  id: string;
  type: "step_sla_timeout" | "dispatch_timeout" | "step_advanced" | "module_activation" | "generic";
  case_id?: string;
  message: string;
  timestamp: string;
  read: boolean;
}

const TYPE_ICONS: Record<string, string> = {
  step_sla_timeout: "⏱",
  dispatch_timeout: "🚨",
  step_advanced: "➡",
  module_activation: "🔔",
  generic: "ℹ",
};

const TYPE_COLORS: Record<string, string> = {
  step_sla_timeout: "#d97706",
  dispatch_timeout: "#dc2626",
  step_advanced: "#2563eb",
  module_activation: "#7c3aed",
  generic: "#64748b",
};

function loadPersisted(): Notification[] {
  try {
    const raw = localStorage.getItem("aeos_notifications");
    if (!raw) return [];
    return JSON.parse(raw) as Notification[];
  } catch { return []; }
}

function persist(notifs: Notification[]): void {
  try {
    localStorage.setItem("aeos_notifications", JSON.stringify(notifs.slice(0, 50)));
  } catch { /* silent */ }
}

interface Props {
  open: boolean;
  onClose: () => void;
  onNavigateToCase?: (caseId: string) => void;
  unreadCount: number;
  onUnreadCountChange: (count: number) => void;
}

export function NotificationDrawer({ open, onClose, onNavigateToCase, onUnreadCountChange }: Props): React.ReactElement {
  const [notifications, setNotifications] = useState<Notification[]>(loadPersisted);
  const socketRef = useRef<Socket | null>(null);

  // Update parent unread count whenever notifications change
  useEffect(() => {
    const unread = notifications.filter((n) => !n.read).length;
    onUnreadCountChange(unread);
  }, [notifications, onUnreadCountChange]);

  // Initialize Socket.IO subscription once
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const socket: Socket = io("/", {
      auth: { token },
      transports: ["websocket"],
      autoConnect: true,
    });

    socketRef.current = socket;

    function addNotification(raw: Record<string, unknown>): void {
      const notif: Notification = {
        id: `n-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: (raw["type"] as Notification["type"]) ?? "generic",
        case_id: raw["case_id"] as string | undefined,
        message: String(raw["message"] ?? raw["detail"] ?? "Alert received"),
        timestamp: String(raw["timestamp"] ?? new Date().toISOString()),
        read: false,
      };
      setNotifications((prev) => {
        const updated = [notif, ...prev].slice(0, 50);
        persist(updated);
        return updated;
      });
    }

    socket.on("case:alert", addNotification);
    socket.on("alert:new", addNotification);

    return () => {
      socket.off("case:alert", addNotification);
      socket.off("alert:new", addNotification);
      socket.disconnect();
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent): void { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  function markAllRead(): void {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, read: true }));
      persist(updated);
      return updated;
    });
  }

  function markRead(id: string): void {
    setNotifications((prev) => {
      const updated = prev.map((n) => n.id === id ? { ...n, read: true } : n);
      persist(updated);
      return updated;
    });
  }

  function clearAll(): void {
    setNotifications([]);
    persist([]);
  }

  const unread = notifications.filter((n) => !n.read).length;

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.25)",
            zIndex: 199,
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      {/* Drawer panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: 420, zIndex: 200,
        background: "#fff",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid var(--border, #e2e8f0)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🔔</span>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "var(--ink, #0f172a)" }}>
              Notifications
              {unread > 0 && (
                <span style={{
                  marginLeft: 8, background: "#dc2626", color: "#fff",
                  borderRadius: "9999px", padding: "2px 8px",
                  fontSize: 12, fontWeight: 700, verticalAlign: "middle",
                }}>
                  {unread}
                </span>
              )}
            </h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ fontSize: 12, color: "var(--brand, #6366f1)", border: "none", background: "none", cursor: "pointer", fontWeight: 600 }}>
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button onClick={clearAll} style={{ fontSize: 12, color: "var(--muted, #64748b)", border: "none", background: "none", cursor: "pointer" }}>
                Clear
              </button>
            )}
            <button onClick={onClose} aria-label="Close" style={{ fontSize: 20, color: "var(--muted)", border: "none", background: "none", cursor: "pointer", lineHeight: 1, padding: "2px 6px" }}>
              ×
            </button>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {notifications.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: "var(--muted, #64748b)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔕</div>
              <div style={{ fontWeight: 600 }}>No notifications yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Alerts will appear here as cases progress.</div>
            </div>
          ) : (
            notifications.map((n) => (
              <div
                key={n.id}
                onClick={() => {
                  markRead(n.id);
                  if (n.case_id && onNavigateToCase) {
                    onNavigateToCase(n.case_id);
                    onClose();
                  }
                }}
                style={{
                  padding: "12px 20px",
                  borderBottom: "1px solid var(--border, #e2e8f0)",
                  background: n.read ? "#fff" : `${TYPE_COLORS[n.type] ?? "#6366f1"}08`,
                  cursor: n.case_id ? "pointer" : "default",
                  transition: "background 0.15s ease",
                  display: "flex", gap: 12, alignItems: "flex-start",
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                  background: `${TYPE_COLORS[n.type] ?? "#6366f1"}18`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14,
                }}>
                  {TYPE_ICONS[n.type] ?? "ℹ"}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--ink, #0f172a)", lineHeight: 1.45, marginBottom: 4 }}>
                    {!n.read && <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: TYPE_COLORS[n.type] ?? "#6366f1", marginRight: 6, verticalAlign: "middle", marginBottom: 1 }} />}
                    {n.message}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted, #64748b)", display: "flex", gap: 8 }}>
                    {n.case_id && <span style={{ color: "var(--brand, #6366f1)", fontWeight: 500 }}>{n.case_id}</span>}
                    <span>{new Date(n.timestamp).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
